import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MISReportDownload = () => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    // Validate dates
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    // Validate 6 month range
    if (startDate && endDate) {
      const diffMonths = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24 * 30);
      if (diffMonths > 6) {
        toast.error('Date range cannot exceed 6 months');
        return;
      }
    }

    try {
      setDownloading(true);
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        },
        responseType: 'blob'
      };

      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get(`${API}/tasks/report/download`, {
        ...config,
        params
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tasks_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
      setOpen(false);
      setStartDate('');
      setEndDate('');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error.response?.data?.detail || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  // Set default dates (today and 1 month ago)
  const getDefaultDates = () => {
    const today = new Date().toISOString().split('T')[0];
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const oneMonthAgoStr = oneMonthAgo.toISOString().split('T')[0];
    
    return { start: oneMonthAgoStr, end: today };
  };

  const handleOpen = () => {
    const defaults = getDefaultDates();
    setStartDate(defaults.start);
    setEndDate(defaults.end);
    setOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (isOpen) {
        handleOpen();
      } else {
        setOpen(false);
      }
    }}>
      <DialogTrigger asChild>
        <Button 
          className="bg-green-600 hover:bg-green-700 gap-2"
          data-testid="download-report-btn"
        >
          <FileSpreadsheet className="w-4 h-4" />
          Download Report
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Download MIS Report</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Download task details as CSV/Excel file. Select a date range (maximum 6 months).
          </p>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Start Date
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={downloading}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                End Date
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={downloading}
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-xs text-blue-800">
              <strong>Report includes:</strong> Task Name, Description, Status, Assigned User, Customer Name, Location, Created Date, Deadline
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={downloading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-green-600 hover:bg-green-700 gap-2"
            >
              <Download className="w-4 h-4" />
              {downloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MISReportDownload;
